import React, { useEffect, useMemo, useState } from "react";
import { formatDecimal } from "./CondicionesFinancieras";
import { pagosApi } from "./pagosApi";

export const CATEGORIA_LABELS = {
  CALCULABLE: "Calculable",
  BLOQUEADO: "Bloqueado",
  EXCEPCION: "Excepción",
  PAGO_MANUAL_PENDIENTE: "Pago manual pendiente",
  SIN_BENEFICIARIO: "Sin beneficiario válido",
  DATOS_INVALIDOS: "Datos inválidos",
  NO_PAGA: "No paga (neto ≤ 0)",
};

export const COBERTURA_LABELS = {
  COMPLETO: "Completo",
  SIN_OPERACIONES_JUSTIFICADO_CARGOS: "Sin ventas · sólo cargos",
  SIN_OPERACIONES_SIN_EXPLICAR: "Sin operaciones sin explicar",
  OPERACIONES_SIN_PROCESAR: "Operaciones sin procesar",
  LECTURA_EXCEDENTE: "Lectura excedente",
  ERROR: "Error",
};

const TASA_LABELS = {
  DERIVADA_FISERV: "Derivada de Fiserv",
  SIN_ANTICIPO: "Sin anticipo",
  CONSTANTE_APROBADA: "Constante aprobada",
  INCOMPLETA: "Incompleta",
};

/** Fracción en texto ("0.0278396") → porcentaje con 4 decimales ("2,7839 %"), moviendo la coma sobre el texto. */
export const formatTasa = (valor) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  const raw = String(valor);
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return raw;
  const negativo = raw.startsWith("-");
  const [entero, fraccion = ""] = raw.replace("-", "").split(".");
  const frac = fraccion.padEnd(6, "0");
  const parteEntera = String(Number.parseInt(`${entero}${frac.slice(0, 2)}`, 10));
  return `${negativo ? "-" : ""}${parteEntera},${frac.slice(2, 6)} %`;
};

const SEVERIDAD_CLASE = { ERROR: "pagos-issue--error", WARNING: "pagos-issue--warning", INFO: "pagos-issue--info" };
const ESTADO_ARCHIVO_CLASE = { PROCESADO: "pagos-status--safe", REPETIDO_EN_LA_CARGA: "pagos-status--warning", NO_SOPORTADO: "pagos-status--warning", RECHAZADO: "pagos-status--warning" };
const formatFecha = (value) => {
  const [year, month, day] = String(value || "").slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : "—";
};
const POR_PAGINA = 100;

function Resultado({ resultado }) {
  const [familia, setFamilia] = useState("");
  const [soloIncompletas, setSoloIncompletas] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [categoria, setCategoria] = useState("");
  const operaciones = useMemo(() => (resultado.operaciones || [])
    .filter((op) => (!familia || op.familia === familia) && (!soloIncompletas || op.estado !== "CALCULADO")), [resultado, familia, soloIncompletas]);
  const visibles = operaciones.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA);
  const grupos = (resultado.fur?.grupos || []).filter((grupo) => !categoria || grupo.categoria === categoria);
  const conteoCategorias = (resultado.fur?.grupos || []).reduce((acc, grupo) => ({ ...acc, [grupo.categoria]: (acc[grupo.categoria] || 0) + 1 }), {});
  const r = resultado.resumen;
  const c = resultado.comparacion;
  const cob = resultado.coberturaImportacion;
  const prep = resultado.preparacion;

  return (
    <section aria-label="Resultado de la liquidación desde archivos">
      <div className="pagos-section-heading">
        <div><h3>Lote SAS {formatFecha(resultado.fechaPago)} · desde archivos originales</h3>
          <p>{resultado.snapshot ? `Snapshot ${resultado.snapshot.archivo}` : "Sin snapshot: motor no ejecutado"} · motor {resultado.motorVersion}</p></div>
        <div className="pagos-header-badges">
          <span className="pagos-status pagos-status--warning">{c?.veredicto === "VALIDADO" ? "VALIDADO" : "NO VALIDADO"}</span>
          <span className="pagos-mode-badge">Corrida temporal · sin obligaciones ni TXT</span>
        </div>
      </div>

      {prep && <div className={`pagos-alert ${prep.estado === "BLOQUEADA" ? "pagos-alert--error" : "pagos-alert--info"}`} role="status" aria-label="Preparación definitiva">
        <strong>Preparación definitiva: {prep.estado === "BLOQUEADA" ? "BLOQUEADA" : "Lista para revisión"}</strong>
        {(prep.motivos || []).length > 0 && <ul>{prep.motivos.map((item) => <li key={item}>{item}</li>)}</ul>}
        <small className="pagos-cell-note">{prep.nota}</small>
      </div>}

      {(resultado.bloqueos || []).length > 0 && <div className="pagos-alert pagos-alert--error" role="status"><strong>Bloqueos</strong>
        <ul>{resultado.bloqueos.map((item) => <li key={item}>{item}</li>)}</ul></div>}

      <div className="pagos-summary pagos-summary--demo">
        <div className="pagos-summary-card"><span>Operaciones normalizadas</span><strong>{r.operaciones}</strong><small>{r.duplicadas} duplicadas · {r.sinBeneficiario} sin beneficiario</small></div>
        <div className="pagos-summary-card"><span>Bruto desde archivos</span><strong>{formatDecimal(r.bruto)}</strong><small>Histórico de la fecha: {r.historicoOperaciones} ops · {formatDecimal(r.historicoBruto)}</small></div>
        <div className="pagos-summary-card"><span>Motor autónomo</span><strong>{r.calculadas ?? "—"} calculadas</strong><small>{r.incompletas ?? "—"} incompletas</small></div>
        <div className="pagos-summary-card"><span>FUR preliminar</span><strong>{r.netoPreliminar === null || r.netoPreliminar === undefined ? "Bloqueado" : formatDecimal(r.netoPreliminar)}</strong>
          <small>{r.grupos ?? 0} grupos · {r.beneficiarios ?? 0} beneficiarios · parcial {formatDecimal(r.netoPreliminarParcial)}</small></div>
        <div className="pagos-summary-card"><span>Excepción de anticipo</span><strong>{r.enExcepcionAnticipo}</strong><small>operaciones de terminales en excepción</small></div>
      </div>

      {cob && <>
        <h4 className="pagos-subheading">Cobertura de importación</h4>
        <div className="pagos-summary pagos-summary--demo" aria-label="Cobertura de importación">
          <div className="pagos-summary-card"><span>PDF recibidos</span><strong>{cob.pdfRecibidos}</strong><small>{cob.pdfReconocidos} reconocidos · {cob.pdfRepetidos} repetidos</small></div>
          <div className="pagos-summary-card"><span>PDF procesados</span><strong>{cob.pdfProcesados}</strong><small>{cob.pdfSinOperacionesJustificadas} sin ventas justificadas</small></div>
          <div className="pagos-summary-card"><span>Con problemas</span><strong>{cob.pdfConOperacionesSinProcesar + cob.pdfSinOperacionesSinExplicar + cob.pdfConErrores}</strong>
            <small>{cob.pdfConOperacionesSinProcesar} sin procesar · {cob.pdfSinOperacionesSinExplicar} sin explicar · {cob.pdfConErrores} con error</small></div>
          <div className="pagos-summary-card"><span>Duplicados y faltantes</span><strong>{cob.operacionesDuplicadas}</strong>
            <small>{(cob.fuentesFaltantes || []).length ? `Faltan: ${cob.fuentesFaltantes.join(", ")}` : "Sin fuentes faltantes"}</small></div>
        </div>
        <div className="pagos-table-wrap"><table aria-label="Operaciones por tarjeta"><thead><tr><th>Tarjeta</th><th>Operaciones</th><th>Bruto</th></tr></thead><tbody>
          {(cob.porTarjeta || []).map((item) => <tr key={item.tarjeta}><td>{item.tarjeta}</td><td>{item.operaciones}</td><td>{formatDecimal(item.bruto)}</td></tr>)}
        </tbody></table></div>
      </>}

      {(resultado.tasasAnticipo || []).length > 0 && <>
        <h4 className="pagos-subheading">Cálculo de anticipo (Total Descuento)</h4>
        <p className="pagos-cell-note">Regla del Conversor: el cociente Dto. Financ. / Ventas sale del PDF Fiserv; las tasas fijas se usan sólo con una regla aprobada vigente.</p>
        <div className="pagos-table-wrap"><table aria-label="Cálculo de anticipo"><thead><tr><th>Estado</th><th>Caso</th><th>Operaciones</th><th>Bruto</th><th>Motivo</th></tr></thead><tbody>
          {resultado.tasasAnticipo.map((item) => (
            <tr key={`${item.estado}-${item.caso}`} className={item.estado === "INCOMPLETA" ? "pagos-row--review" : ""}>
              <td>{TASA_LABELS[item.estado] || item.estado}</td><td>{item.caso}</td><td>{item.operaciones}</td><td>{formatDecimal(item.bruto)}</td><td><small>{item.motivo || "—"}</small></td>
            </tr>
          ))}
        </tbody></table></div>
      </>}

      <h4 className="pagos-subheading">Archivos y fuentes</h4>
      <div className="pagos-table-wrap"><table aria-label="Archivos de la carga"><thead><tr>
        <th>Archivo</th><th>Tipo · proveedor</th><th>Familia</th><th>Estado</th><th>Cobertura</th><th>Operaciones</th><th>Bruto</th><th>Liquidación</th><th>Detalle</th>
      </tr></thead><tbody>
        {(resultado.archivos || []).map((archivo) => (
          <tr key={`${archivo.contenedor || ""}-${archivo.nombre}-${archivo.sha256}`}>
            <td>{archivo.nombre}<small className="pagos-cell-note">{archivo.contenedor ? `dentro de ${archivo.contenedor} · ` : ""}SHA {archivo.sha256}…</small></td>
            <td>{archivo.tipo} · {archivo.proveedor || "—"}</td><td>{archivo.familia || "—"}</td>
            <td><span className={`pagos-status ${ESTADO_ARCHIVO_CLASE[archivo.estado] || ""}`}>{archivo.estado}</span></td>
            <td>{archivo.cobertura ? <>{COBERTURA_LABELS[archivo.cobertura] || archivo.cobertura}
              <small className="pagos-cell-note">{archivo.filasLeidas}/{archivo.filasDetalle} filas{archivo.cargos ? ` · ${archivo.cargos} cargos` : ""}{(archivo.transaccionesNoSoportadas || []).length ? ` · ${archivo.transaccionesNoSoportadas.join(", ")}` : ""}</small></> : "—"}</td>
            <td>{archivo.operaciones}</td><td>{formatDecimal(archivo.bruto)}</td>
            <td>{archivo.numeroLiquidacion || "—"}<small className="pagos-cell-note">{archivo.fechaLiquidacion ? formatFecha(archivo.fechaLiquidacion) : ""}</small></td>
            <td><small>{archivo.motivo}</small></td>
          </tr>
        ))}
      </tbody></table></div>

      <div className="pagos-table-wrap"><table aria-label="Cobertura por familia"><thead><tr>
        <th>Familia</th><th>Archivos</th><th>Operaciones</th><th>Bruto</th><th>Lector</th><th>En el lote de referencia</th>
      </tr></thead><tbody>
        {(resultado.cobertura || []).map((item) => (
          <tr key={item.familia} className={item.esperadaPorReferencia && item.archivos === 0 ? "pagos-row--review" : ""}>
            <td>{item.familia}</td><td>{item.archivos}</td><td>{item.operaciones}</td><td>{formatDecimal(item.bruto)}</td><td>{item.soporte}</td>
            <td>{item.esperadaPorReferencia ? (item.archivos === 0 ? "Sí · FALTA" : "Sí") : "No"}</td>
          </tr>
        ))}
      </tbody></table></div>
      <p className="pagos-cell-note">Referencia de familias: {resultado.referenciaFamilias}.</p>

      {(resultado.incidencias || []).length > 0 && <>
        <h4 className="pagos-subheading">Incidencias</h4>
        <ul className="pagos-issues">{resultado.incidencias.map((item, index) => (
          <li key={`${item.code}-${index}`} className={`pagos-issue ${SEVERIDAD_CLASE[item.severity] || ""}`}><strong>{item.code}</strong> {item.message}</li>
        ))}</ul>
      </>}

      {(resultado.reglasAplicadas || []).length > 0 && <>
        <h4 className="pagos-subheading">Reglas aprobadas para pruebas consideradas</h4>
        <ul>{resultado.reglasAplicadas.map((regla) => <li key={regla.origen}>{regla.origen} · {regla.tipo} {regla.sujeto} {String(regla.porcentaje).replace(".", ",")} % · {regla.operaciones} operaciones</li>)}</ul>
      </>}

      {resultado.fur && <>
        <h4 className="pagos-subheading">FUR preliminar</h4>
        <div className="pagos-filter-grid">
          <select aria-label="Filtrar categoría" value={categoria} onChange={(event) => setCategoria(event.target.value)}>
            <option value="">Todas las categorías</option>
            {Object.entries(CATEGORIA_LABELS).map(([key, label]) => <option key={key} value={key}>{label} ({conteoCategorias[key] || 0})</option>)}
          </select>
        </div>
        <div className="pagos-table-wrap"><table aria-label="Grupos del FUR preliminar"><thead><tr>
          <th>Comercio</th><th>CUIT · cuenta</th><th>Operaciones</th><th>Bruto</th><th>Neto</th><th>Categoría</th><th>Incidencias</th>
        </tr></thead><tbody>
          {grupos.map((grupo) => (
            <tr key={grupo.id} className={grupo.categoria === "CALCULABLE" ? "" : "pagos-row--review"}>
              <td><strong>{grupo.nombreComercio}</strong><small className="pagos-cell-note">{grupo.razonSocial}</small></td>
              <td>{grupo.cuit}<small className="pagos-cell-note">{grupo.tipoCuenta} {grupo.cuenta}</small></td>
              <td>{grupo.operaciones}{grupo.incompletas > 0 && <small className="pagos-cell-note">{grupo.incompletas} incompletas</small>}</td>
              <td>{formatDecimal(grupo.bruto)}</td><td>{grupo.neto === null ? "Bloqueado" : formatDecimal(grupo.neto)}</td>
              <td>{CATEGORIA_LABELS[grupo.categoria] || grupo.categoria}</td>
              <td><small>{(grupo.incidencias || []).map((issue) => issue.message).join(" · ") || "—"}</small></td>
            </tr>
          ))}
        </tbody></table></div>
      </>}

      {c && <>
        <h4 className="pagos-subheading">Comparación contra el histórico de la fecha (base_dashboard)</h4>
        <p className="pagos-cell-note">Sólo se compara: los importes históricos no se usan como entrada del cálculo.</p>
        <div className="pagos-table-wrap"><table aria-label="Comparación por componente desde archivos"><thead><tr>
          <th>Componente</th><th>Exactos</th><th>Distintos</th><th>Incompletos</th><th>Sin dato histórico</th>
        </tr></thead><tbody>
          {(c.operaciones.porComponente || []).map((item) => (
            <tr key={item.componente}><td>{item.componente}</td><td>{item.exactos}</td><td>{item.distintos}</td><td>{item.incompletos}</td><td>{item.noDisponibles}</td></tr>
          ))}
        </tbody></table></div>
        <p className="pagos-cell-note">Operaciones emparejadas {c.operaciones.emparejadas} · sólo en archivos {c.operaciones.soloMotor} · sólo en histórico {c.operaciones.soloHistorico} ·
          netos exactos {c.operaciones.netosExactos} · cuentas distintas {c.operacionesConCuentaDistinta} · grupos sólo históricos {c.gruposSoloHistorico}</p>
        <ul>{(c.motivos || []).map((item) => <li key={item}>{item}</li>)}</ul>
      </>}

      <h4 className="pagos-subheading">Operaciones normalizadas ({operaciones.length})</h4>
      <div className="pagos-filter-grid">
        <select aria-label="Filtrar familia" value={familia} onChange={(event) => { setFamilia(event.target.value); setPagina(0); }}>
          <option value="">Todas las familias</option>
          {(resultado.cobertura || []).map((item) => <option key={item.familia} value={item.familia}>{item.familia}</option>)}
        </select>
        <label><input type="checkbox" aria-label="Sólo incompletas" checked={soloIncompletas} onChange={(event) => { setSoloIncompletas(event.target.checked); setPagina(0); }} /> Sólo incompletas</label>
        <span className="pagos-cell-note">Página {pagina + 1} de {Math.max(1, Math.ceil(operaciones.length / POR_PAGINA))}</span>
        <button type="button" className="pagos-secondary" disabled={pagina === 0} onClick={() => setPagina(pagina - 1)}>Anterior</button>
        <button type="button" className="pagos-secondary" disabled={(pagina + 1) * POR_PAGINA >= operaciones.length} onClick={() => setPagina(pagina + 1)}>Siguiente</button>
      </div>
      <div className="pagos-table-wrap"><table aria-label="Operaciones normalizadas"><thead><tr>
        <th>Origen</th><th>Terminal · lote · cupón</th><th>Fecha</th><th>Tarjeta · cuotas</th><th>Bruto</th><th>Fiserv (arancel / financ.)</th><th>Total Descuento</th><th>Beneficiario</th><th>Motor</th>
      </tr></thead><tbody>
        {visibles.map((op) => (
          <tr key={op.id} className={op.estado === "CALCULADO" ? "" : "pagos-row--review"}>
            <td>{op.archivo}<small className="pagos-cell-note">pág. {op.pagina} · fila {op.fila}{op.duplicada ? " · DUPLICADA" : ""}</small></td>
            <td>{op.terminal} · {op.lote} · {op.cupon}</td><td>{formatFecha(op.fechaOperacion)}</td>
            <td>{op.tarjeta} · {op.cuotas ?? "—"}<small className="pagos-cell-note">{op.transaccionFiserv}</small></td>
            <td>{formatDecimal(op.bruto)}</td><td>{formatDecimal(op.dtoArancelFiserv)} / {formatDecimal(op.dtoFinancieroFiserv)}</td>
            <td>{op.tasaAnticipo ? <>{op.tasaAnticipo.valor === null ? (TASA_LABELS[op.tasaAnticipo.estado] || op.tasaAnticipo.estado) : formatTasa(op.tasaAnticipo.valor)}
              <small className="pagos-cell-note">{op.tasaAnticipo.caso}{op.tasaAnticipo.regla ? ` · ${op.tasaAnticipo.regla}` : ""}</small></> : "—"}</td>
            <td>{op.cuit ? <>{op.comercio}<small className="pagos-cell-note">{op.cuit}{op.excepcionAnticipo ? " · excepción anticipo" : ""}</small></> : <small>{op.beneficiario}</small>}</td>
            <td>{op.estado === "CALCULADO" ? formatDecimal(op.neto) : op.estado}<small className="pagos-cell-note">{(op.incompletitudes || []).join(" ")}</small></td>
          </tr>
        ))}
      </tbody></table></div>
    </section>
  );
}

/**
 * Carga de la liquidación SAS desde archivos originales: ZIP o PDF Fiserv (y adicionales, que se
 * identifican y se informan). El backend normaliza, liquida con el motor y arma el FUR preliminar
 * en una corrida temporal. La pantalla sólo envía archivos e IDs.
 */
export default function LiquidacionArchivos({ token }) {
  const [estado, setEstado] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotId, setSnapshotId] = useState("");
  const [fecha, setFecha] = useState("");
  const [archivos, setArchivos] = useState([]);
  const [corridas, setCorridas] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let vigente = true;
    Promise.resolve().then(() => pagosApi.estadoCondiciones(token))
      .then(async (response) => {
        if (!vigente) return;
        setEstado(response || { habilitada: false });
        if (response?.habilitada) {
          const lista = await pagosApi.snapshotsCondiciones(token);
          if (vigente) { setSnapshots(lista || []); setSnapshotId(lista?.[0]?.id || ""); }
        }
      })
      .catch((requestError) => { if (vigente) setEstado({ habilitada: false, motivo: requestError?.message }); });
    return () => { vigente = false; };
  }, [token]);

  const ejecutar = async (clave, accion) => {
    setError(""); setLoading(clave);
    try {
      await accion();
    } catch (requestError) {
      setError(requestError?.message || "No se pudo completar la operación.");
    } finally {
      setLoading("");
    }
  };

  const elegirFecha = (valor) => {
    setFecha(valor); setResultado(null);
    if (valor) ejecutar("corridas", async () => setCorridas(await pagosApi.archivosCorridas(valor, token) || []));
  };

  const procesar = () => ejecutar("procesar", async () => {
    const corrida = await pagosApi.archivosProcesar({ fechaPago: fecha, snapshotId, archivos }, token);
    setCorridas(await pagosApi.archivosCorridas(fecha, token) || []);
    setResultado(await pagosApi.archivosResultado(corrida.id, token));
  });

  const ver = (id) => ejecutar("ver", async () => setResultado(await pagosApi.archivosResultado(id, token)));

  if (!estado) return <section className="pagos-panel" data-testid="liquidacion-archivos"><p>Cargando…</p></section>;
  if (!estado.habilitada) {
    return (
      <section className="pagos-panel" data-testid="liquidacion-archivos">
        <h2>Liquidación desde archivos</h2>
        <div className="pagos-alert pagos-alert--warning" role="status">El procesamiento desde archivos sólo funciona en el entorno aislado de desarrollo. {estado.motivo}</div>
      </section>
    );
  }

  return (
    <section className="pagos-panel" aria-labelledby="pagos-archivos-title" data-testid="liquidacion-archivos">
      <div className="pagos-section-heading">
        <div>
          <h2 id="pagos-archivos-title">Liquidación SAS desde archivos originales</h2>
          <p>Adjuntá el ZIP de liquidaciones diarias Fiserv (o los PDF). Se identifican proveedor y tarjeta, se normalizan las operaciones y se liquidan con el motor autónomo.</p>
        </div>
        <span className="pagos-status pagos-status--warning">Simulación · no escribe en bases productivas</span>
      </div>
      {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}

      <div className="pagos-filter-grid">
        <label className="pagos-date"><span>Fecha de pago</span>
          <input aria-label="Fecha de pago desde archivos" type="date" value={fecha} onChange={(event) => elegirFecha(event.target.value)} /></label>
        <label className="pagos-date"><span>Canal</span>
          <select aria-label="Canal" value="SAS" disabled><option value="SAS">SAS Fiserv</option></select></label>
        <label className="pagos-date"><span>Snapshot de Listas</span>
          <select aria-label="Snapshot para archivos" value={snapshotId} onChange={(event) => setSnapshotId(event.target.value)}>
            <option value="">Sin snapshot (sólo normalizar)</option>
            {snapshots.map((item) => <option key={item.id} value={item.id}>{item.archivo}</option>)}
          </select></label>
      </div>
      <div className="pagos-filter-grid">
        <label className="pagos-date"><span>Archivos de liquidación (ZIP/PDF Fiserv y adicionales)</span>
          <input aria-label="Archivos de liquidación" type="file" multiple accept=".zip,.pdf,.xlsx,.xlsm,.xls,.csv,.txt"
            onChange={(event) => setArchivos(Array.from(event.target.files || []))} /></label>
        <button type="button" className="pagos-primary" disabled={!fecha || !archivos.length || loading === "procesar"} onClick={procesar}>
          {loading === "procesar" ? "Procesando archivos…" : "Procesar archivos"}</button>
      </div>
      {archivos.length > 0 && <p className="pagos-cell-note">{archivos.length} archivos seleccionados: {archivos.map((archivo) => archivo.name).join(", ")}</p>}

      {corridas.length > 0 && <>
        <h3 className="pagos-subheading">Corridas temporales de la fecha</h3>
        <div className="pagos-table-wrap"><table aria-label="Corridas desde archivos"><thead><tr>
          <th>Creada</th><th>Estado</th><th>Archivos</th><th>Operaciones</th><th>Bruto</th><th>Grupos</th><th>Registró</th><th></th>
        </tr></thead><tbody>
          {corridas.map((item) => (
            <tr key={item.id}><td>{String(item.creadoEn).replace("T", " ").slice(0, 16)}</td><td>{item.estado}</td><td>{item.archivos}</td>
              <td>{item.operaciones - item.incompletas}/{item.operaciones}</td><td>{formatDecimal(String(item.bruto))}</td><td>{item.grupos}</td><td>{item.creadoPor}</td>
              <td><button type="button" className="pagos-secondary" onClick={() => ver(item.id)}>Ver</button></td></tr>
          ))}
        </tbody></table></div>
      </>}

      {resultado && <Resultado resultado={resultado} />}
    </section>
  );
}
